import { useEffect, useRef, type MutableRefObject } from 'react';
import { useDispatch } from 'react-redux';
import * as lessonActions from 'Actions/lesson';
import { NotifyPhaserEvents } from 'Phaser/EventBus2';
import {
  playTimeNoteFlushController,
  type SightReadingTransforms,
} from 'Models/PlayTimeNoteFlushController';
import type { ParallelModeConversion } from 'Utils/MusicXmlModeConversion';
import type { AppDispatch } from 'Reducers';

type SightReadingTransformChange = {
  transposeSemitones: number;
  modeConversion: ParallelModeConversion;
  playSessionId: string | null | undefined;
  // What the phrases are currently built under; read when a phrase is served
  // and when a play-time batch is cut.
  transformsRef: MutableRefObject<SightReadingTransforms>;
};

// Live transpose / mode change: pause so the clock re-syncs on the next play,
// then let the PhraseManager re-transpose the on-screen phrases in place (no
// exercise reset / remount). The notes played so far are cut into a batch
// under the old transposition first: the rebuild resets the event stream, and
// the pause flush would run after it, too late.
export const useSightReadingTransformChange = ({
  transposeSemitones,
  modeConversion,
  playSessionId,
  transformsRef,
}: SightReadingTransformChange): void => {
  const dispatch = useDispatch<AppDispatch>();
  const transformKey = `${transposeSemitones}:${modeConversion}`;
  const previousTransformKeyRef = useRef(transformKey);

  useEffect(() => {
    if (previousTransformKeyRef.current === transformKey) {
      return;
    }
    previousTransformKeyRef.current = transformKey;

    playTimeNoteFlushController.flush('settings').catch(() => {});
    transformsRef.current = { transposeSemitones, modeConversion };

    if (!playSessionId) {
      return;
    }

    dispatch(lessonActions.setLessonPlaying({ lessonPlaying: false }));
    NotifyPhaserEvents.SetSightReadingTranspose({
      semitones: transposeSemitones,
      modeConversion,
    });
  }, [dispatch, playSessionId, transformKey, transposeSemitones, modeConversion, transformsRef]);
};
